import type React from "react";

import {
  ComboboxList,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxCollection,
  ComboboxEmpty,
} from "src/primitives/Combobox";
import { Spinner } from "src/primitives/Spinner";
import { OptionItem } from "./OptionItem";
import type { DropdownPassthroughProps } from "./MultiSelectCombobox.types";

interface MultiSelectDropdownProps extends DropdownPassthroughProps {
  renderOption: (val: string) => React.ReactNode;
  testIdPrefix?: string;
  children?: React.ReactNode;
}

export function MultiSelectDropdown({
  resolvedOptions,
  flatOptionsMap,
  isGrouped,
  isLoading,
  loadingMessage,
  emptyMessage,
  showCreateOption,
  renderOption,
  testIdPrefix,
  hasMoreLazy,
  loaderCallbackRef,
  children,
}: MultiSelectDropdownProps) {
  return (
    <ComboboxList
      {...(testIdPrefix
        ? { "data-testid": `${testIdPrefix}-select-menu` }
        : {})}
    >
      {isLoading && (
        <div className="flex w-full justify-center py-2 text-center text-sm text-muted-foreground">
          {loadingMessage}
        </div>
      )}
      {!isLoading && !showCreateOption && (
        <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
      )}
      {children ??
        (resolvedOptions.length > 0 || showCreateOption ? (
          isGrouped ? (
            <ComboboxCollection>
              {(group: { label: string; items: string[] }) => (
                <ComboboxGroup
                  key={group.label || "__create__"}
                  items={group.items}
                >
                  {group.label && <ComboboxLabel>{group.label}</ComboboxLabel>}
                  <ComboboxCollection>
                    {(val: string) => (
                      <OptionItem
                        key={val}
                        val={val}
                        flatOptionsMap={flatOptionsMap}
                        renderOption={renderOption}
                        testIdPrefix={testIdPrefix}
                      />
                    )}
                  </ComboboxCollection>
                </ComboboxGroup>
              )}
            </ComboboxCollection>
          ) : (
            <ComboboxCollection>
              {(val: string) => (
                <OptionItem
                  key={val}
                  val={val}
                  flatOptionsMap={flatOptionsMap}
                  renderOption={renderOption}
                  testIdPrefix={testIdPrefix}
                />
              )}
            </ComboboxCollection>
          )
        ) : null)}
      {hasMoreLazy && (
        <div
          ref={loaderCallbackRef}
          className="flex w-full items-center justify-center py-3"
          data-testid={
            testIdPrefix ? `${testIdPrefix}-lazy-loader` : "lazy-loader"
          }
        >
          <Spinner />
        </div>
      )}
    </ComboboxList>
  );
}
